import { faChevronDown, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronDown, Loader2, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Option } from "react-multi-select-component";

interface IProps {
  options: Option[];
  data?: Option[];
  setData: (data: Option[]) => void;
  labelText?: string;
  isRequired?: boolean;
  extraClass?: string;
  placeholder?: string;
  disableSearch?: boolean;
  optionId?: (item: Option, index: number) => string;
  dropdownMainClass?: string;
  dropdownMainId?: string;
  newUI?: boolean;
  loading?: boolean;
  clearId?: string;
  isMulti?: boolean;
  dropdownInnerStyle?: React.CSSProperties;
  disabled?: boolean;
  error?: string;
  dropdownInnerClass?: string;
  dropdownPosition?: "top" | "bottom";
  mainDivClass?: string;
  withoutMargin?: boolean;
  showRadioBtn?: boolean;
  clearIcon?: boolean;
  isSearch?: boolean;
  isSearchClass?: string;
  outSearchable?: boolean;
  onSearchChange?: (value: string) => void;
  innerLabel?: string;
  readOnly?: boolean;
  dropdownMainStyle?: React.CSSProperties;
  withoutTopMargin?: boolean;
}

const CustomWebsiteDropdown = ({
  options,
  data = [],
  setData = () => {},
  labelText = "",
  isRequired,
  extraClass,
  placeholder,
  disableSearch,
  optionId,
  dropdownMainClass,
  dropdownMainId,
  newUI,
  loading,
  clearId,
  isMulti,
  dropdownInnerStyle = {},
  disabled,
  error,
  dropdownInnerClass,
  dropdownPosition = "bottom",
  mainDivClass,
  withoutMargin,
  showRadioBtn,
  clearIcon = true,
  isSearch = false,
  isSearchClass,
  outSearchable = false,
  onSearchChange,
  innerLabel,
  readOnly,
  dropdownMainStyle,
  withoutTopMargin,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const dropdownRef = useRef<HTMLButtonElement>(null);
  let dropdownPositionClass =
    dropdownPosition === "top"
      ? "bottom-full 2xl:mb-1 mb-0.5"
      : "top-full 2xl:mt-1 mt-0.5";

  useEffect(() => {
    const windowClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        dropdownRef.current.contains(e.target as Node)
      ) {
        return;
      }
      setIsOpen(false);
    };
    window.addEventListener("click", windowClick);
    return () => window.removeEventListener("click", windowClick);
  }, []);

  return (
    <div className={`w-full max-w-full ${mainDivClass || ""}`}>
      {labelText && (
        <label
          className={`label-color-select-city ${
            newUI
              ? "font-Poppins-Medium text-[#2f2f31] font-size-16px"
              : "font-Poppins-Regular font-size-18px"
          }`}
        >
          {isRequired && <span className="text-[#FF0000]">* </span>}
          {labelText}
        </label>
      )}
      <div
        className={`w-full relative select-none text-[#2F2F31] font-Poppins-Regular ${newUI ? "font-size-15px" : "font-size-16px"} ${
          withoutMargin ? "" : withoutTopMargin ? "mb-[1rem] mt-0" : "mb-[1rem] input-field-margin-top"
        } ${extraClass || ""}`}
      >
        <button
          disabled={loading || disabled || readOnly}
          className={`w-full flex items-center justify-between rounded-[5px] border border-[#707070] ${
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          } bg-white ${
            newUI
              ? "!border !border-[#ACACAC] 2xl:!rounded-[10px] !rounded-[6px]"
              : ""
          } ${dropdownMainClass || ""}`}
          id={dropdownMainId || ""}
          ref={dropdownRef}
          onClick={() => {
            // e.stopPropagation();
            if (loading) return;
            setIsOpen(!isOpen);
          }}
          style={{
            ...dropdownMainStyle,
          }}
          type="button"
        >
          <div
            className={`p-1.5 2xl:p-2 ${
              !clearIcon && !loading
                ? "w-[calc(100%-30px)]"
                : "w-[calc(100%-60px)]"
            }
             ${dropdownInnerClass || ""}`}
            style={{
              ...dropdownInnerStyle,
            }}
          >
            {innerLabel ? (
              <p className="text-gray-500 h-full flex items-center text-left w-full whitespace-nowrap font-size-14px">
                {innerLabel || ""}
              </p>
            ) : null}
            {data && data.length > 0 && !loading && data[0]?.label ? (
              <div className="w-full flex items-center gap-1 truncate text-left h-full">
                {isMulti
                  ? data.map((item: Option) => item.label).join(", ")
                  : data[0]?.label}
              </div>
            ) : (
              <p className="text-gray-500 h-full flex items-center text-left w-full whitespace-nowrap">
                {placeholder || "Select Option"}
              </p>
            )}
          </div>
          <div className="flex items-center justify-end pr-[0.5rem] w-[60px]">
            {loading ? (
              <Loader2 className="animate-spin" color="gray" />
            ) : (
              data.length > 0 &&
              !loading &&
              clearIcon && (
                <FontAwesomeIcon
                  icon={faXmark}
                  color="gray"
                  className="cursor-pointer font-size-16px scale-110"
                  onClick={(e) => {
                    e.stopPropagation();
                    setData([]);
                    setIsOpen(false);
                  }}
                  id={`clear-dropdown-${clearId || ""}`}
                />
              )
            )}
            <FontAwesomeIcon
              icon={faChevronDown}
              color="gray"
              className={`${
                isOpen ? "scale-[-1]" : ""
              } font-size-15px transition-all`}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              id="dropdown-icon"
            />
          </div>
        </button>
        {isOpen && (
          <div
            className={`w-full absolute shadow-md overflow-hidden ${dropdownPositionClass} left-0 z-[1] bg-white rounded-[5px] border border-[#E0E0E0] max-h-[200px] overflow-y-auto scrollbar-5`}
          >
            {isSearch && (
              <div className="w-full mb-0.5 flex items-center gap-1.5 px-2 py-1 bg-[#F5F5F5]">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    if (onSearchChange) {
                      onSearchChange(e.target.value);
                    }
                  }}
                  placeholder="Search"
                  id={`${dropdownMainId || "dropdown"}-search-input`}
                  className={`w-full ${
                    isSearchClass || ""
                  } border-none outline-none bg-transparent py-1`}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  autoFocus={true}
                />
                {searchValue ? (
                  <X
                    size={18}
                    color="gray"
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchValue("");
                      if (onSearchChange) {
                        onSearchChange("");
                      }
                    }}
                    id={`${dropdownMainId || "dropdown"}-search-clear-btn`}
                  />
                ) : null}
              </div>
            )}
            {options && options.length > 0 ? (
              options
                .filter((item: Option) =>
                  !outSearchable
                    ? item.label
                        .toLowerCase()
                        .includes(searchValue.toLowerCase())
                    : true
                )
                .map((item: Option, index) => (
                  <button
                    key={item.value}
                    disabled={readOnly || item.disabled}
                    type="button"
                    id={
                      optionId
                        ? optionId(item, index)
                        : `${dropdownMainId || ""}-option-${index + 1}`
                    }
                    className={`w-full flex items-center justify-between gap-1.5 ${
                      showRadioBtn
                        ? "2xl:p-2 lg:p-1.5 p-1"
                        : "2xl:p-3 lg:p-2 p-1.5"
                    } ${
                      item.disabled
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer hover:bg-[#F5F5F5]"
                    }  ${
                      data.find(
                        (dataItem: Option) => dataItem.value === item.value
                      ) && !showRadioBtn
                        ? "bg-[#F5F5F5]"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isMulti) {
                        if (
                          data.find(
                            (dataItem: Option) => dataItem.value === item.value
                          )
                        ) {
                          setData(
                            data.filter(
                              (dataItem: Option) =>
                                dataItem.value !== item.value
                            )
                          );
                        } else {
                          setData([...data, item]);
                        }
                      } else {
                        setData([item]);
                        setIsOpen(false);
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      {isMulti ? (
                        <input
                          type="checkbox"
                          checked={
                            data.find(
                              (dataItem: Option) =>
                                dataItem.value === item.value
                            )
                              ? true
                              : false
                          }
                          className="accent-[#00443F]"
                        />
                      ) : null}
                      <span className={`text-[#2F2F31] text-left font-Poppins-Regular ${newUI ? "font-size-15px" : "font-size-16px"}`}>
                        {item.label}
                      </span>
                    </div>
                    {showRadioBtn && (
                      <input
                        type="radio"
                        checked={
                          data.find(
                            (dataItem: Option) => dataItem.value === item.value
                          )
                            ? true
                            : false
                        }
                        className="ml-auto accent-[#00443F]"
                      />
                    )}
                  </button>
                ))
            ) : (
              <div className="w-full flex items-center justify-center p-3">
                <span className="text-gray-500">No Options</span>
              </div>
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="text-red-500 -mt-3 mb-1 font-size-14px font-Poppins-Regular">
          {error}
        </p>
      )}
    </div>
  );
};

export default CustomWebsiteDropdown;
